const MIN_INTERVAL = 1000;
const MAX_WORLDS = 32;
const validRevision = value => Number.isSafeInteger(value) && value >= 0;
const validId = value => typeof value === 'string' && value.length > 0 && value.length <= 256;
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);

/** Revision-only content subscription, independent of player/presence state.
 *
 * api(path, body, 'POST') must reject unsuccessful HTTP responses. Call poll()
 * each frame; it never rejects and starts at most one request per second, with
 * one network request in flight. No timers or listeners outlive the caller.
 * getKnownWorlds returns a Map<id, definition> or definition[]. The first 32
 * unique valid worlds are subscribed; put the current/visible worlds first.
 *
 * onWorld(world, {isCurrent}) and onCatalog(summaries, {catalogRevision,
 * isCurrent}) may prepare asynchronously. They MUST check isCurrent() after
 * every await and immediately before committing changes. Keep player, camera,
 * movement and input objects outside these callbacks' definition-only updates.
 * A callback already running cannot be undone; its guard prevents a superseded
 * preparation, disconnect, hidden tab or disposal from committing afterward.
 *
 * Requests continue while callbacks prepare. Per-world and catalog callbacks
 * are serialized, newer queued revisions replace older ones, and a failed
 * callback is retried with bounded backoff. Only successful callbacks advance
 * acknowledged revisions. poll() resolves when the response is accepted, not
 * when asynchronous scene preparation finishes. onError is optional and safe
 * to use for diagnostics; its own errors never escape the frame loop.
 */
export function createLiveContentSync({
  api, getKnownWorlds, onWorld = () => {}, onCatalog = () => {}, onError = () => {},
  isVisible = () => typeof document === 'undefined' || !document.hidden,
  now = () => performance.now(), connected = true, catalogRevision,
  intervalMs = MIN_INTERVAL, retryBaseMs = 1000, retryMaxMs = 16000,
} = {}) {
  if (typeof api !== 'function' || typeof getKnownWorlds !== 'function') {
    throw new TypeError('Live content sync requires api and getKnownWorlds');
  }
  if (typeof onWorld !== 'function' || typeof onCatalog !== 'function') {
    throw new TypeError('Live content callbacks must be functions');
  }
  const interval = Math.max(MIN_INTERVAL, Number.isFinite(intervalMs) ? intervalMs : MIN_INTERVAL);
  const retryBase = Math.max(MIN_INTERVAL, Number.isFinite(retryBaseMs) ? retryBaseMs : MIN_INTERVAL);
  const retryMax = Math.max(retryBase, Number.isFinite(retryMaxMs) ? retryMaxMs : 16000);
  const lane = revision => ({revision, pending: null, applying: false, failures: 0, retryAt: -Infinity});
  const worlds = new Map(), catalog = lane(validRevision(catalogRevision) ? catalogRevision : undefined);
  const obsolete = Symbol('obsolete live content request');
  let disposed = false, generation = 0, flight = null, reconnectPending = false;
  let lastRequestAt = -Infinity, requestRetryAt = -Infinity, requestFailures = 0;
  connected = Boolean(connected);

  const delayFor = failures => Math.min(retryMax, retryBase * 2 ** Math.min(Math.max(0, failures - 1), 20));
  function report(error, scope, worldId) {
    try { Promise.resolve(onError(error, {scope, ...(worldId ? {worldId} : {})})).catch(() => {}); }
    catch { /* Diagnostic hooks must not break cleanup or frame polling. */ }
  }
  function active() { return !disposed && connected && isVisible(); }
  function knownWorlds() {
    const source = getKnownWorlds();
    if (!(source instanceof Map) && !Array.isArray(source)) {
      throw new TypeError('getKnownWorlds must return a Map or array of world definitions');
    }
    const known = new Map();
    const entries = source instanceof Map ? source.entries() : source.map(world => [world?.id, world]);
    for (const [id, world] of entries) {
      if (!validId(id) || !object(world) || world.id !== id || !validRevision(world.revision) || known.has(id)) continue;
      known.set(id, world.revision);
      if (known.size === MAX_WORLDS) break;
    }
    return known;
  }
  function prune(known) {
    for (const [id, record] of worlds) {
      if (!known.has(id)) { record.pending = null; worlds.delete(id); }
    }
  }
  function current(record, entry, id) {
    try {
      if (!active() || entry.generation !== generation || record.pending !== entry) return false;
      if (id === undefined) return (catalog.revision ?? -1) <= entry.revision;
      const known = knownWorlds();
      return worlds.get(id) === record && known.has(id) && known.get(id) <= entry.revision
        && (record.revision ?? -1) <= entry.revision;
    } catch { return false; }
  }
  function offer(record, value, revision) {
    if (revision <= (record.revision ?? -1) || revision <= (record.pending?.revision ?? -1)) return;
    record.pending = {value, revision, generation};
    // A new revision should not inherit an older definition's preparation error.
    record.failures = 0; record.retryAt = -Infinity;
  }
  function pump(record, id) {
    const entry = record.pending;
    if (!entry || record.applying || !active() || now() < record.retryAt) return;
    if (!current(record, entry, id)) {
      // A separate creation/portal/presence path may already have a newer world.
      record.pending = null;
      return;
    }
    record.applying = true;
    const context = {isCurrent: () => current(record, entry, id),
      ...(id === undefined ? {catalogRevision: entry.revision} : {})};
    void Promise.resolve().then(() => {
      if (!context.isCurrent()) return;
      return id === undefined ? onCatalog(entry.value, context) : onWorld(entry.value, context);
    }).then(() => {
      if (!context.isCurrent()) return;
      record.revision = entry.revision; record.pending = null;
      record.failures = 0; record.retryAt = -Infinity;
    }).catch(error => {
      if (!context.isCurrent()) return;
      record.failures++; record.retryAt = now() + delayFor(record.failures);
      report(error, id === undefined ? 'catalog' : 'world', id);
    }).finally(() => {
      record.applying = false;
      if (!disposed) {
        // This launches only the newest queued revision, never each intermediate.
        try { pump(record, id); } catch (error) { report(error, 'callback', id); }
      }
    });
  }
  function pumpAll() {
    pump(catalog);
    for (const [id, record] of worlds) pump(record, id);
  }
  function accept(data, known) {
    if (!object(data) || !validRevision(data.catalogRevision) || !Array.isArray(data.changedWorlds)
      || data.changedWorlds.length > MAX_WORLDS || (data.worlds !== undefined && !Array.isArray(data.worlds))) {
      throw new Error('Invalid live content response');
    }
    const newestCatalog = Math.max(catalog.revision ?? -1, catalog.pending?.revision ?? -1);
    if (data.catalogRevision > newestCatalog && !Array.isArray(data.worlds)) {
      throw new Error('Changed catalog is missing world summaries');
    }
    if (data.worlds?.some(world => !object(world) || !validId(world.id))) {
      throw new Error('Invalid live catalog summaries');
    }
    if (data.changedWorlds.some(world => !object(world) || !validId(world.id) || !validRevision(world.revision))) {
      throw new Error('Invalid changed world definition');
    }
    const currentKnown = knownWorlds();
    prune(currentKnown);
    if (data.worlds && data.catalogRevision > newestCatalog) offer(catalog, data.worlds, data.catalogRevision);
    for (const world of data.changedWorlds) {
      if (!known.has(world.id) || !currentKnown.has(world.id) || world.revision <= currentKnown.get(world.id)) continue;
      let record = worlds.get(world.id);
      if (!record) { record = lane(currentKnown.get(world.id)); worlds.set(world.id, record); }
      offer(record, world, world.revision);
    }
    pumpAll();
    return {catalogRevision: data.catalogRevision, changedWorlds: data.changedWorlds.length};
  }
  function poll() {
    try {
      if (!active()) return Promise.resolve({skipped: disposed ? 'disposed' : connected ? 'hidden' : 'disconnected'});
      pumpAll();
      if (flight) return flight;
      const time = now();
      if (time < lastRequestAt + interval || time < requestRetryAt) return Promise.resolve({skipped: 'throttled'});
      const known = knownWorlds();
      prune(known);
      const sent = Object.fromEntries([...known].map(([id, revision]) => [id, Math.max(revision, worlds.get(id)?.revision ?? 0)]));
      const requestGeneration = generation;
      const body = {knownWorldRevisions: sent, ...(catalog.revision === undefined ? {} : {catalogRevision: catalog.revision})};
      lastRequestAt = time; reconnectPending = false;
      flight = Promise.resolve().then(() => {
        if (!active() || generation !== requestGeneration) return obsolete;
        return api('/api/changes', body, 'POST');
      }).then(data => {
        if (!active() || generation !== requestGeneration || data === obsolete) return {skipped: 'obsolete'};
        const result = accept(data, known);
        requestFailures = 0; requestRetryAt = -Infinity;
        return result;
      }).catch(error => {
        if (disposed || generation !== requestGeneration) return {skipped: 'obsolete'};
        requestFailures++; requestRetryAt = now() + delayFor(requestFailures);
        report(error, 'request');
        return {error};
      }).finally(() => {
        flight = null;
        if (reconnectPending && !disposed) poll();
      });
      return flight;
    } catch (error) {
      requestFailures++; requestRetryAt = now() + delayFor(requestFailures);
      report(error, 'poll');
      return Promise.resolve({error});
    }
  }

  return {
    poll,
    setConnected(value) {
      value = Boolean(value);
      if (disposed || connected === value) return Promise.resolve({skipped: disposed ? 'disposed' : 'unchanged'});
      connected = value; generation++;
      catalog.pending = null;
      for (const record of worlds.values()) record.pending = null;
      requestFailures = 0; requestRetryAt = -Infinity;
      reconnectPending = value;
      // The first eligible request starts now. If a request just started, the
      // next frame observes the same one-second bound rather than burst polling.
      return value ? poll() : Promise.resolve({skipped: 'disconnected'});
    },
    getStats: () => ({connected, disposed, inFlight: Boolean(flight), catalogRevision: catalog.revision,
      trackedWorlds: worlds.size, pendingWorlds: [...worlds.values()].filter(record => record.pending).length,
      requestFailures, retryAt: requestRetryAt}),
    dispose() {
      if (disposed) return;
      disposed = true; connected = false; generation++; reconnectPending = false;
      catalog.pending = null;
      for (const record of worlds.values()) record.pending = null;
      worlds.clear();
    },
  };
}
